import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useDispatch } from 'react-redux';
import {
  CANCEL_BUTTON_TEXT,
  SHOW_NOTIFICATION,
} from '../../../../constants';
import { DialogBoxComponent } from '../../../../components/DialogBox/DialogBoxCard';
import {
  createLeaveGroup,
  updateLeaveGroup,
} from '../../../Services/MasterdataService';
import BandConfigurationEditor from './BandConfigurationEditor';
import {
  bandsToEditorState,
  editorStateToBandConfiguration,
  emptyBandEditorState,
  validateBandEditorState,
} from './bandConfigurationUtils';

const emptyForm = () => ({
  groupName: '',
  isActive: true,
});

const LeaveGroupForm = (props) => {
  const { type, data, refresh, setRefresh, handleFormClose } = props;
  const dispatch = useDispatch();

  const [form, setForm] = useState(emptyForm());
  const [bandEditor, setBandEditor] = useState(emptyBandEditorState());
  const [fieldErrors, setFieldErrors] = useState({});
  const [bandErrors, setBandErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loadingApiCall, setLoadingApiCall] = useState(false);

  useEffect(() => {
    if (type === 'update' && data) {
      setForm({
        id: data.id,
        groupName: data.groupName ?? '',
        isActive: data.isActive !== false,
      });
      setBandEditor(bandsToEditorState(data.bandConfiguration));
    } else {
      setForm(emptyForm());
      setBandEditor(emptyBandEditorState());
    }
  }, [type, data]);

  const dispatchNotification = (typ, msg) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: {
        open: true,
        type: typ,
        message: msg,
      },
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleActiveToggle = (event) => {
    setForm((prev) => ({ ...prev, isActive: event.target.checked }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.groupName || !String(form.groupName).trim()) {
      nextErrors.groupName = 'Group name is required';
    }

    const bandValidation = validateBandEditorState(bandEditor);
    setFieldErrors(nextErrors);
    setBandErrors(bandValidation.errors);

    return Object.keys(nextErrors).length === 0 && bandValidation.isValid;
  };

  const buildPayload = () => ({
    ...(type === 'update' ? { id: form.id } : {}),
    groupName: String(form.groupName).trim(),
    isActive: form.isActive !== false,
    bandConfiguration: editorStateToBandConfiguration(bandEditor),
  });

  const isDeactivating =
    type === 'update' && data?.isActive !== false && form.isActive === false;

  const onSubmitClick = () => {
    if (!validateForm()) {
      return;
    }
    setConfirmOpen(true);
    setLoadingApiCall(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setLoadingApiCall(false);
  };

  const onConfirmSave = async () => {
    setConfirmOpen(false);
    const payload = buildPayload();
    setLoadingApiCall(true);
    const response =
      type === 'update'
        ? await updateLeaveGroup(payload)
        : await createLeaveGroup(payload);
    setLoadingApiCall(false);

    if (response.isError === false) {
      setRefresh(!refresh);
      handleFormClose();
    }
    dispatchNotification(response.type, response.message);
  };

  const confirmContent = () => {
    if (type !== 'update') {
      return `Do you want to create Leave Group "${form.groupName}"?`;
    }
    if (isDeactivating) {
      return (
        <Typography variant="body1" style={{ color: 'red' }}>
          This soft-deactivates &quot;{form.groupName}&quot;. Employees already
          assigned keep their assignment, but inactive groups are skipped for
          future quarterly grants. Historical GRANTED balances are not changed.
        </Typography>
      );
    }
    return `Do you want to update Leave Group "${form.groupName}"? Existing quarterly grants will not be rewritten.`;
  };

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {type === 'update' ? 'Edit Leave Group' : 'Create Leave Group'}
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              required
              name="groupName"
              label="Group Name"
              value={form.groupName}
              onChange={handleInputChange}
              error={Boolean(fieldErrors.groupName)}
              helperText={fieldErrors.groupName || ''}
            />
          </Grid>
          {type === 'update' ? (
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive !== false}
                    onChange={handleActiveToggle}
                    color="primary"
                  />
                }
                label={form.isActive !== false ? 'Active' : 'Inactive'}
              />
            </Grid>
          ) : null}
        </Grid>

        <BandConfigurationEditor
          value={bandEditor}
          onChange={(next) => {
            setBandEditor(next);
            setBandErrors({});
          }}
          errors={bandErrors}
        />

        <Grid container spacing={2} justifyContent="space-between" sx={{ mt: 1 }}>
          <Grid item>
            <Button variant="outlined" color="error" onClick={handleFormClose}>
              {CANCEL_BUTTON_TEXT}
            </Button>
          </Grid>
          <Grid item>
            <LoadingButton
              variant="contained"
              loading={loadingApiCall}
              onClick={onSubmitClick}
            >
              {loadingApiCall
                ? 'loading...'
                : type === 'update'
                  ? 'Update Leave Group'
                  : 'Create Leave Group'}
            </LoadingButton>
          </Grid>
        </Grid>
      </Box>

      <DialogBoxComponent
        open={confirmOpen}
        handleClose={handleConfirmClose}
        title={isDeactivating ? 'Deactivate Leave Group?' : undefined}
        confirmText="Yes"
        cancelText="No"
        content={confirmContent()}
        onConfirm={onConfirmSave}
        onCancel={handleConfirmClose}
      />
    </>
  );
};

export default LeaveGroupForm;
